import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "./daylight-insight.css";
import { exportDashboardXlsx, exportXlsx } from "@/lib/utils/daylight-insight/export";
import { apiToMetric, deltaOf } from "@/components/daylight-insight/api/metricsAdapt";
import type { Route } from "@/lib/utils/daylight-insight/types";
import { DaylightStoreProvider, useStore } from "@/provider/store";
import { BrandRow } from "./BrandRow";
import { DrillPanel } from "./DrillPanel";

const ROUTE_PATH: Record<Route, string> = {
  guide: "/guide-view-insight",
  operating: "/dashboard-insight",
  "all-metrics": "/all-metrics-insight",
  charlie: "/charlie-insight",
};

function routeFromPath(pathname: string): Route {
  if (pathname.startsWith("/guide-view-insight")) return "guide";
  if (pathname.startsWith("/all-metrics-insight")) return "all-metrics";
  if (pathname.startsWith("/charlie-insight")) return "charlie";
  return "operating";
}

function LayoutInner() {
  const s = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const route = routeFromPath(location.pathname);

  const moduleName = (s.service === "hh" ? "Home Health" : "Hospice") + " · " +
    (route === "guide" ? "Metrics Guide"
      : route === "all-metrics" ? "All Metrics"
      : route === "charlie" ? "Charlie"
      : "Operating View");
  useEffect(() => { document.title = "DayLight · " + moduleName; }, [moduleName]);

  // The bootstrap config (service lines, branches, payors, categories) has to be
  // in hand before the shell means anything — rendering early would show a
  // dashboard with no branches and a guessed set of tabs.
  const booting = s.configLoading && !s.serviceLines.length;

  // Export follows the screen: when a pillar tab has published its live metrics,
  // write just that dashboard — plus one further sheet per detail table that
  // dashboard is showing (By Branch, By Clinician, …). Otherwise (Guide /
  // All Metrics / Current State, or metrics still loading) fall back to the
  // seeded workbook.
  const handleExport = () => {
    if (s.activeDashboard) {
      exportDashboardXlsx(
        s.activeDashboard.title,
        s.activeDashboard.metrics.map((dm) => ({
          metric: apiToMetric(dm),
          value: dm.currentValue,
          delta:
            dm.currentValue === null || dm.priorValue === null
              ? null
              : deltaOf(dm.currentValue, dm.priorValue),
        })),
        s.range,
        s.getDetailTables(),
      );
      return;
    }
    exportXlsx(s.reg, s.sections, s.snap, s.range);
  };

  return (
    <>
      <div className="dl" style={{ background: "var(--canvas)", minHeight: "100vh" }}>
        {/* Charlie is a full-screen destination — it ships its own header, and
            stacking the DayLight brand row above it would mean two brand marks
            and two service toggles. Its logo navigates back to the Operating
            View, which is the way back to this nav. */}
        {route !== "charlie" && (
        <div className="dl-header">
          <BrandRow
            route={route}
            setRoute={(r) => navigate(ROUTE_PATH[r])}
            service={s.service}
            setService={s.setService}
            // All Metrics exports the active tab from its own toolbar, so the
            // header button stands down there rather than competing with it.
            // Guide has nothing of its own to write — the button would only ever
            // emit the seeded workbook — so it's off there for now too.
            onExport={
              route === "all-metrics" || route === "guide" ? undefined : handleExport
            }
          />
        </div>
        )}
        {booting ? (
          <div className="dl-boot">Loading your dashboard…</div>
        ) : s.configError ? (
          <div className="dl-boot">
            <div>{s.configError}</div>
            <button className="dl-ctrl" style={{ marginTop: 14 }} onClick={s.refetchConfig}>Retry</button>
          </div>
        ) : (
          <Outlet />
        )}
        {s.drillStack.length > 0 && <DrillPanel />}
      </div>
    </>
  );
}

export default function DaylightLayout() {
  return (
    <DaylightStoreProvider>
      <LayoutInner />
    </DaylightStoreProvider>
  );
}
